import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpContext, HttpContextToken } from '@angular/common/http';
import { Observable, throwError, EMPTY, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AdminAuthService } from '../admin/service/admin-auth.service';
import { isSameOriginAssetRequest, normalizeApiEndpoint } from '../shared/utils/api-endpoint.util';
import { reportApiError } from '../shared/utils/rum-error.util';
import { trackRumAction } from '../shared/utils/rum-user.util';

// In-memory marker (not a header): a custom header is not in API Gateway's CORS allow-list and fails the preflight.
export const RETRY_AFTER_REFRESH = new HttpContextToken<boolean>(() => false);

export interface ApiError {
  status: number;
  error: string;
  message: string;
}

@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor {
  constructor(private router: Router, private adminAuth: AdminAuthService) {}

  private static parseBody(raw: unknown): any {
    if (typeof raw !== 'string') return raw;
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }

  private static report(request: HttpRequest<unknown>, status: number, code: string): string {
    const endpoint = normalizeApiEndpoint(request.url, { headers: request.headers });
    // Same-origin static assets (e.g. /assets/local-services.json) are not API calls: no api_error.
    if (isSameOriginAssetRequest(request.url)) return endpoint;
    reportApiError({ endpoint, status, code, method: request.method });
    return endpoint;
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Angular XHR backend wraps a JSON-parse failure on a 2xx response as:
        // HttpErrorResponse { status: 200, error: { error: SyntaxError, text: '<rawBody>' } }
        // A network-level parse error comes as:
        // HttpErrorResponse { status: 0, error: SyntaxError }
        const parsedError: SyntaxError | undefined =
          error.status === 200 && error.error?.error instanceof SyntaxError
            ? error.error.error
            : error.status === 0 && error.error instanceof SyntaxError
              ? error.error
              : undefined;

        const isHtmlAsJson =
          parsedError !== undefined &&
          (parsedError.message.includes('is not valid JSON') ||
           parsedError.message.includes("Unexpected token '<'"));

        if (isHtmlAsJson) {
          const endpoint = ErrorHandlerInterceptor.report(request, 502, 'NON_JSON_RESPONSE');
          console.warn('[HTTP] non-JSON (HTML) response treated as 502:', endpoint);
          return throwError(() => new HttpErrorResponse({
            status: 502,
            statusText: 'Bad Gateway (non-JSON response)',
            url: request.url
          }));
        }

        const isAdminRoute = this.router.url.startsWith('/admin');

        if (error.status === 401 && isAdminRoute && !request.url.includes('/auth/me')) {
          if (request.context.get(RETRY_AFTER_REFRESH)) {
            ErrorHandlerInterceptor.report(request, 401, 'SESSION_EXPIRED');
            this.adminAuth.logout();
            this.router.navigate(['/admin/login']);
            return EMPTY;
          }

          return from(this.adminAuth.silentRefresh()).pipe(
            switchMap(() => {
              // HttpContext.set mutates in place: copy it so the original request (re-subscribable) stays unmarked.
              const context = new HttpContext();
              for (const key of request.context.keys()) context.set(key, request.context.get(key));
              const retryReq = request.clone({
                setHeaders: { 'Authorization': `Bearer ${this.adminAuth.getToken()}` },
                context: context.set(RETRY_AFTER_REFRESH, true)
              });
              return next.handle(retryReq);
            }),
            catchError(() => {
              ErrorHandlerInterceptor.report(request, 401, 'SESSION_EXPIRED');
              this.adminAuth.logout();
              this.router.navigate(['/admin/login']);
              return EMPTY;
            })
          );
        }

        // The sparring bot page (Recall Output Media) has no human to leave /maintenance-screen:
        // its 503 is handled by the page itself.
        const isSparringBotRoute = this.router.url.startsWith('/sparring-bot');
        if (error.status === 503 && !isAdminRoute && !isSparringBotRoute) {
          // Maintenance is not an error: no api_error, only a RUM action. Skip if already on the screen
          // or if the status probe itself returned 503 (avoids a redirect loop). returnUrl is path-only: the query/fragment may hold tokens.
          const current = this.router.url;
          if (!current.startsWith('/maintenance-screen') && !request.url.includes('/api/v2/maintenance/status')) {
            trackRumAction('maintenance_redirect', { source: 'http_503', endpoint: normalizeApiEndpoint(request.url) });
            this.router.navigate(['/maintenance-screen'], { queryParams: { returnUrl: current.split(/[?#]/)[0] } });
          }
          return EMPTY;
        }

        // Envelope legacy (~70 lambdas): { error: string, message: string }
        // Envelope nuevo (Process, API_DESIGN_GUIDELINES.md): { error: { code, message, details? }, metadata }
        // Ambos formatos deben convivir — no todas las lambdas migraron al envelope nuevo.
        // Con responseType 'text' (streaming NDJSON de fc-low) el body de error llega
        // como string: se intenta parsear a JSON para no perder el mensaje del server.
        const body = ErrorHandlerInterceptor.parseBody(error.error);
        const nestedError = body?.error;
        const isNewEnvelope = nestedError !== null && typeof nestedError === 'object';

        const apiError: ApiError = {
          status: error.status,
          error: (isNewEnvelope ? nestedError.code : nestedError) || error.statusText || 'UNKNOWN_ERROR',
          // Never Angular's `error.message`: it embeds the full request URL (emails, tokens).
          message: (isNewEnvelope ? nestedError.message : body?.message) || error.statusText || 'An error occurred'
        };
        const code = error.status === 0 ? 'NETWORK_ERROR' : apiError.error;
        const endpoint = ErrorHandlerInterceptor.report(request, error.status, code);
        console.warn('[HTTP] api_error', { endpoint, status: error.status, code });
        return throwError(() => apiError);
      })
    );
  }
}
